import React, { useState } from "react";
import { Form, Modal, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import api from "../../api";
import { taskDisplayTitle } from "../../utils/taskDisplay";

// "Edit Due Date" for the task's assignee (My Tasks + Task Detail).
// PATCH /tasks/:id/due-date changes ONLY due_at; the server re-checks
// everything below (assignee, open status, tasks:update_due_date) -- this
// file only decides whether to offer the control.

// Same open statuses the server accepts (services/taskDueDateService.js).
export const DUE_DATE_EDITABLE_STATUSES = new Set(["pending", "assigned", "in_progress", "development_started", "development_completed"]);

const normStatus = (s) => String(s || "pending").trim().toLowerCase().replace(/[\s-]+/g, "_");

export function canEditOwnDueDate(task, user, can) {
  if (!task || !user || typeof can !== "function") return false;
  // List rows carry assigned_to as an id; Task Detail as { id, name }.
  const assigneeId = task.assigned_to && typeof task.assigned_to === "object" ? task.assigned_to.id : task.assigned_to;
  if (assigneeId === null || assigneeId === undefined) return false;
  if (Number(assigneeId) !== Number(user.id)) return false;
  if (!DUE_DATE_EDITABLE_STATUSES.has(normStatus(task.status))) return false;
  return can("tasks", "update_due_date");
}

const pad = (n) => String(n).padStart(2, "0");

// due_at as served (ISO with Z, or naive "YYYY-MM-DD HH:mm:ss") -> the
// local wall time a datetime-local input shows.
export function toLocalInputValue(dueAt) {
  if (!dueAt) return "";
  const d = new Date(String(dueAt).includes("T") || String(dueAt).endsWith("Z") ? dueAt : String(dueAt).replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Server reply "2026-10-20 18:00:00" (naive local) -> "2026-10-20T18:00:00",
// which every date helper in the app parses as local time.
export const fromServerDueAt = (value) => String(value || "").replace(" ", "T");

const formatCurrent = (dueAt) => {
  if (!dueAt) return "Not set";
  const d = new Date(dueAt);
  if (Number.isNaN(d.getTime())) return "Not set";
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export function EditDueDateModal({ task, onClose, onUpdated }) {
  const [value, setValue] = useState(() => toLocalInputValue(task.due_at));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    if (!value) { setError("Choose a new due date and time."); return; }
    setSaving(true);
    setError("");
    try {
      const res = await api.patch(`/tasks/${task.id}/due-date`, { due_at: value.length === 16 ? `${value}:00` : value });
      const dueAt = fromServerDueAt(res.data?.data?.due_at || value);
      toast.success("Due date updated.");
      onUpdated?.(dueAt);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not update the due date.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show onHide={saving ? undefined : onClose} centered size="sm">
      <Modal.Header closeButton={!saving}>
        <Modal.Title as="h6" className="mb-0">Edit Due Date</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="small text-muted mb-2">{taskDisplayTitle(task)}</p>
        <p className="small mb-2">Current: <strong>{formatCurrent(task.due_at)}</strong></p>
        <Form.Group controlId={`due-date-${task.id}`}>
          <Form.Label className="small">New Due Date</Form.Label>
          <Form.Control type="datetime-local" value={value} onChange={(e) => setValue(e.target.value)} disabled={saving} />
        </Form.Group>
        {error && <div className="alert alert-danger small mt-3 mb-0" role="alert">{error}</div>}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-light btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
        <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={saving || !value}>
          {saving ? <><Spinner animation="border" size="sm" /> Updating…</> : "Update"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

// Small pencil button + modal. Renders nothing unless the user may edit.
export function EditDueDateButton({ task, user, can, onUpdated, className = "" }) {
  const [open, setOpen] = useState(false);
  if (!canEditOwnDueDate(task, user, can)) return null;
  return (
    <>
      <button
        type="button"
        className={`btn btn-link btn-sm p-0 ms-1 align-baseline due-date-edit-btn ${className}`}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        aria-label={`Edit due date of ${taskDisplayTitle(task) || `task ${task.id}`}`}
        title="Edit due date"
      >
        <i className="bi bi-pencil" aria-hidden="true" />
      </button>
      {open && (
        <EditDueDateModal
          task={task}
          onClose={() => setOpen(false)}
          onUpdated={(dueAt) => { setOpen(false); onUpdated?.(dueAt); }}
        />
      )}
    </>
  );
}
